import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useSelectedYear } from '../year/useYear'
import { useAsync } from '../lib/useAsync'
import {
  defaultPeriodForYear, formatMoney, formatPeriod, formatPeriodRange,
  yearOfPeriod,
} from '../lib/format'
import {
  computeWaterfall, chargesInForce, splitNet,
  type RecurringCharge,
} from '../lib/waterfall'
import { Loading, ErrorState } from '../components/States'

function shiftPeriod(period: string, delta: number): string {
  const [y, m] = period.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + delta, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

interface ChargeRow extends RecurringCharge {
  effective_from: string
  effective_to: string | null
  active: boolean
  label_fr: string
  label_en: string
}

async function load(period: string, lang: 'fr' | 'en') {
  const [rent, expenses, charges, rule] = await Promise.all([
    supabase
      .from('v_rent_status')
      .select('amount_received')
      .eq('period', period),
    supabase
      .from('expenses')
      .select('amount')
      .eq('period', period)
      .eq('status', 'approuve'),
    supabase
      .from('recurring_charges')
      .select(
        'code, label_fr, label_en, kind, rate, fixed_amount, base, payee,' +
          ' requires_receipt, sort_order, a_verifier,' +
          ' effective_from, effective_to, active',
      )
      .order('sort_order'),
    supabase
      .from('deposit_split_rules')
      .select(
        'fixed_amount, effective_from, effective_to,' +
          ' fixed:bank_accounts!deposit_split_rules_fixed_account_id_fkey(name),' +
          ' reste:bank_accounts!deposit_split_rules_remainder_account_id_fkey(name)',
      ),
  ])

  if (rent.error) throw rent.error
  if (expenses.error) throw expenses.error
  if (charges.error) throw charges.error

  const rentCollected = (rent.data ?? []).reduce(
    (s, r) => s + Number((r as { amount_received: number }).amount_received ?? 0),
    0,
  )
  const expensesApproved = (expenses.data ?? []).reduce(
    (s, e) => s + Number((e as { amount: number }).amount ?? 0),
    0,
  )

  const toutes = (charges.data ?? []) as unknown as ChargeRow[]
  const enVigueur = chargesInForce(toutes, period).map((c) => ({
    ...c,
    label: lang === 'fr' ? c.label_fr : c.label_en,
    rate: c.rate === null ? null : Number(c.rate),
  }))

  // La regle de repartition peut ne pas etre configuree : ses dates
  // d'effet ne m'ont jamais ete communiquees.
  const jour = `${period}-05`
  const regles = (rule.data ?? []) as unknown as {
    fixed_amount: number
    effective_from: string
    effective_to: string | null
    fixed: { name: string } | null
    reste: { name: string } | null
  }[]
  const regle =
    regles.find(
      (r) => r.effective_from <= jour && (r.effective_to === null || r.effective_to > jour),
    ) ?? null

  return { rentCollected, expensesApproved, charges: enVigueur, regle }
}

/**
 * Reglement du mois : la cascade, dans l'ordre du cahier des charges,
 * avec le taux, la base et le montant de chaque ligne affiches. Toute
 * l'arithmetique est verifiable a l'ecran, sans calculatrice.
 */
export function SettlementScreen({ onBack }: { onBack: () => void }) {
  const { t, lang } = useI18n()
  const { year } = useSelectedYear()
  const [period, setPeriod] = useState(() => defaultPeriodForYear(year))

  const { data, loading, error, reload } = useAsync(
    () => load(period, lang),
    [period, lang],
  )

  const w = data
    ? computeWaterfall(data.rentCollected, data.charges, data.expensesApproved)
    : null

  const split =
    w && data?.regle ? splitNet(w.netToDeposit, data.regle.fixed_amount) : null

  return (
    <div className="screen">
      <button type="button" className="backlink no-print" onClick={onBack}>
        ← {t('back')}
      </button>

      <h1 className="screen__title">{t('settlement_title')}</h1>
      <p className="muted">{formatPeriodRange(period, lang)}</p>

      <div className="monthnav no-print">
        <button
          type="button"
          className="btn btn--ghost"
          disabled={period.endsWith('-01')}
          onClick={() => {
            const p = shiftPeriod(period, -1)
            if (yearOfPeriod(p) === year) setPeriod(p)
          }}
        >
          ← {t('previous')}
        </button>
        <span className="monthnav__label">{formatPeriod(period, lang)}</span>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={period.endsWith('-12')}
          onClick={() => {
            const p = shiftPeriod(period, 1)
            if (yearOfPeriod(p) === year) setPeriod(p)
          }}
        >
          {t('next')} →
        </button>
      </div>

      {loading && <Loading />}
      {error && <ErrorState message={error} onRetry={reload} />}

      {w && (
        <>
          {/* 1. Loyers encaisses */}
          <div className="cascade">
            <div className="cascade__row cascade__row--head">
              <span className="cascade__label">
                <strong>1. {t('settlement_rent_collected')}</strong>
              </span>
              <span className="cascade__amount">
                {formatMoney(w.rentCollected)}
              </span>
            </div>

            {/* 2. Les deductions recurrentes, chacune avec son calcul */}
            {w.lines.map((l, i) => (
              <div key={l.code} className="cascade__row">
                <span className="cascade__label">
                  <strong>
                    {i + 2}. {l.label}
                  </strong>
                  {l.kind === 'pourcentage' && l.rateUsed !== null && (
                    <span className="cascade__detail">
                      {t('settlement_pct_detail', {
                        rate: (l.rateUsed * 100).toFixed(l.rateUsed * 100 % 1 === 0 ? 0 : 2),
                        base: formatMoney(l.baseAmount ?? 0),
                      })}
                    </span>
                  )}
                  {l.payee && (
                    <span className="cascade__detail">
                      {t('settlement_payee')} : {l.payee}
                    </span>
                  )}
                  {l.requiresReceipt && (
                    <span className="chip chip--warn">
                      {t('settlement_receipt_required')}
                    </span>
                  )}
                </span>
                <span className="cascade__amount cascade__amount--minus">
                  − {formatMoney(l.amount)}
                </span>
              </div>
            ))}

            {/* 3. Depenses approuvees uniquement */}
            <div className="cascade__row">
              <span className="cascade__label">
                <strong>
                  {w.lines.length + 2}. {t('settlement_expenses')}
                </strong>
                <span className="cascade__detail">
                  {t('settlement_expenses_detail')}
                </span>
              </span>
              <span className="cascade__amount cascade__amount--minus">
                − {formatMoney(w.expensesApproved)}
              </span>
            </div>

            {/* 4. Net a verser */}
            <div className="cascade__row cascade__row--total">
              <span className="cascade__label">
                <strong>
                  {w.lines.length + 3}. {t('settlement_net')}
                </strong>
              </span>
              <span className="cascade__amount">
                {formatMoney(w.netToDeposit)}
              </span>
            </div>
          </div>

          {/* Rien d'autre ne reduit le net : la regle est rappelee. */}
          <div className="banner banner--warn">
            <span>{t('settlement_rule')}</span>
          </div>

          {/* La repartition entre les deux comptes, si la regle est
              configuree. Ses dates d'effet ne m'ont pas ete donnees. */}
          {split && data?.regle ? (
            <div className="card">
              <h2>{t('settlement_split')}</h2>
              <dl className="deflist">
                <dt>{data.regle.fixed?.name ?? ''}</dt>
                <dd>{formatMoney(split.fixed)}</dd>
                <dt>{data.regle.reste?.name ?? ''}</dt>
                <dd>{formatMoney(split.remainder)}</dd>
              </dl>
              <p className="muted">{t('settlement_split_detail')}</p>
            </div>
          ) : (
            <div className="banner banner--warn">
              <strong>{t('settlement_no_split')}</strong>
              <span>{t('settlement_no_split_body')}</span>
            </div>
          )}

          {w.netToDeposit < 0 && (
            <div className="banner banner--error">
              <strong>{t('settlement_negative')}</strong>
              <span>{t('settlement_negative_body')}</span>
            </div>
          )}
        </>
      )}
    </div>
  )
}
